import { DateTime } from 'luxon'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { FeedbackRating } from '@citruscamps/citrus-client'
import { ActivityList } from '../../../../components/ActivityList/ActivityList'
import { DashboardLayout } from '../../../../components/DashboardLayout/DashboardLayout'
import { DateMacroSelect } from '../../../../components/DateMacroSelect/DateMacroSelect'
import { FeatureFlag } from '../../../../components/FeatureFlag/FeatureFlag'
import { useFlags } from '../../../../components/FeatureFlag/hooks/useFlags'
import { FeedbackRatingIcon } from '../../../../components/FeedbackRating/FeedbackRatingIcon'
import { RadialPlot } from '../../../../components/RadialPlot/RadialPlot'
import { Title } from '../../../../components/Title/Title'
import { TrendPlot } from '../../../../components/TrendPlot/TrendPlot'
import { DefaultDetailsPagination } from '../../../../constants/pagination'
import { ROUTES } from '../../../../constants/routes'
import { useAuth } from '../../../../hooks/useAuth'
import { useFetchActivities } from '../../../../hooks/useFetchActivities'
import {
  FeedbackRatingDetails,
  getAvergaeFeedbackRating,
  getFeedbackRating,
} from '../../../../utils/feedback'
import { formatDefaultLabel, pluralize } from '../../../../utils/formatters'
import { toQueryParams } from '../../../../utils/query-params'
import { FieldCarousel } from '../../../../components/FieldCarousel/FieldCarousel'
import { CelebrationsList } from './components/CelebrationsList'
import { DashboardHeader } from './components/DashboardHeader'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import { NotificationsButton } from './components/NotificationsButton'
import { WhatsNewButton } from './components/WhatsNewButton'
import { useFetchOnboarding } from './hooks/useFetchOnboarding'
import { useFetchProgramAnalytics } from './hooks/useFetchProgramAnalytics'

export const Dashboard = (): React.ReactElement => {
  const { user, program, isSwitching } = useAuth()
  const { flags } = useFlags()
  const {
    isLoading,
    onboarding,
    isRecommendedToReferral,
    isRecommendedToUpgradePlan,
    handleSkip,
    handleSetupAccount,
  } = useFetchOnboarding({
    program,
  })
  const { data: activities, isLoading: isLoadingActivities } = useFetchActivities({
    programId: program?.id,
    pagination: DefaultDetailsPagination,
  })
  const {
    analytics,
    pastAnalytics,
    isLoading: isLoadingAnalytics,
    dateQuery: dateMacro,
    setDateQuery: setDateMacro,
  } = useFetchProgramAnalytics({
    programId: program?.id,
    initialDateQuery: { date_macro: 'last7days', unit: 'daily' },
  })
  const today = DateTime.local()

  if (!program || !user || isSwitching || isLoading) {
    return (
      <DashboardLayout>
        <Title value="Dashboard" />
        <DashboardSkeleton />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Title value="Dashboard" />
      <div className="row justify-content-center">
        <div className="col-11">
          <div className="mt-4 pt-4 pb-3">
            <div className="d-lg-flex">
              <h1>Dashboard</h1>
              <div className="d-lg-inline ms-lg-auto mt-auto">
                <span className="text-body-secondary h5 px-2">
                  {today.toLocaleString(DateTime.DATE_HUGE)}
                </span>
                <WhatsNewButton />
                {program && <NotificationsButton programId={program.id} />}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              {typeof onboarding === 'undefined' ? (
                <div>
                  <div className="rounded rounded-3 p-4 bg-body-tertiary border border-body-secondary">
                    <h3 className="mb-3 text-body">&nbsp;</h3>
                    <div>&nbsp;</div>
                  </div>
                  <div className="col-6 d-none d-lg-block"></div>
                </div>
              ) : (
                <DashboardHeader
                  user={user}
                  program={program}
                  onboarding={onboarding}
                  isLoading={isLoading}
                  isRecommendedToReferral={isRecommendedToReferral}
                  isRecommendedToUpgradePlan={isRecommendedToUpgradePlan}
                  onSkip={handleSkip}
                  onSetupPayout={handleSetupAccount}
                />
              )}
            </div>
          </div>
          <div className="container-fluid">
            <div className="row justify-content-between">
              <div className="col-xl-7 my-5">
                <h5 className="mb-3">Registrations Overview</h5>
                <DateMacroSelect
                  values={dateMacro}
                  program={program}
                  onSelectMacro={(value) => setDateMacro(value)}
                />
                <FieldCarousel className="mt-5" columnClassName="col-12 col-lg-6" length={4}>
                  <div>
                    <TrendPlot
                      title="Total volume"
                      data={analytics?.total_volume.map((v) => ({ x: v.x, y: v.y.major_value }))}
                      pastData={pastAnalytics?.total_volume.map((v) => ({
                        x: v.x,
                        y: v.y.major_value,
                      }))}
                      unit="currency"
                      step={analytics?.unit}
                      currency={
                        analytics?.total_volume.find((v) => v.y.currency_code)?.y.currency_code
                      }
                      language={analytics?.total_volume.find((v) => v.y.language)?.y.language}
                      loading={isLoadingAnalytics}
                      timezone={program.timezone}
                    />
                    <TrendPlot
                      title="New registrations"
                      step={analytics?.unit}
                      data={analytics?.new_registrations}
                      pastData={pastAnalytics?.new_registrations}
                      loading={isLoadingAnalytics}
                      timezone={program.timezone}
                    />
                  </div>
                  <div>
                    <RadialPlot
                      title="Satisfaction"
                      loading={isLoadingAnalytics}
                      data={analytics?.feedback_ratings}
                      onValueClick={
                        flags?.registration_report_enabled
                          ? (value) => {
                              const query: string = toQueryParams({
                                ...dateMacro,
                                feedback_rating: value.id,
                              })
                              const reportUrl =
                                ROUTES.FINANCIALS_REPORT_DETAIL.replace(':type', 'registrations') +
                                `?${query}`
                              window.open(reportUrl, '_self')
                            }
                          : undefined
                      }
                      TickLabel={(p) => {
                        const ratings =
                          analytics?.feedback_ratings
                            .filter((i) => i.id)
                            .map((i) => ({ rating: i.id as FeedbackRating, amount: i.angle })) || []
                        const total: number = ratings.reduce<number>(
                          (t, i) => t + (i.amount || 0),
                          0,
                        )
                        const isSelected: boolean = typeof p.selectedPoint?.id !== 'undefined'
                        const rating: FeedbackRatingDetails | undefined = isSelected
                          ? getFeedbackRating(p.selectedPoint?.id)
                          : getAvergaeFeedbackRating(ratings)
                        const amount: number | undefined = isSelected
                          ? analytics?.feedback_ratings.find((i) => i.id === rating?.id)?.angle || 0
                          : total
                        if (!rating) {
                          return null
                        }
                        return (
                          <>
                            <div className="text-primary">
                              <FeedbackRatingIcon rating={rating?.id} className="me-2" />
                              {!isSelected && <span>Overall </span>}
                              {formatDefaultLabel(
                                rating?.id || p.selectedPoint?.label?.toLowerCase() || '',
                              )}
                            </div>
                            <div className="small text-body-secondary">
                              {amount} {isSelected ? rating?.id : 'total'} response
                              {pluralize(amount)}
                            </div>
                          </>
                        )
                      }}
                    />
                    <TrendPlot
                      title="New connected customers"
                      step={analytics?.unit}
                      data={analytics?.new_customers}
                      pastData={pastAnalytics?.new_customers}
                      loading={isLoadingAnalytics}
                      timezone={program.timezone}
                    />
                  </div>
                  <div>
                    <TrendPlot
                      title="Spend per customer"
                      data={analytics?.spend_per_customer.map((v) => ({
                        x: v.x,
                        y: v.y.major_value,
                      }))}
                      pastData={pastAnalytics?.spend_per_customer.map((v) => ({
                        x: v.x,
                        y: v.y.major_value,
                      }))}
                      unit="currency"
                      step={analytics?.unit}
                      summaryType="average"
                      currency={
                        analytics?.spend_per_customer.find((v) => v.y.currency_code)?.y
                          .currency_code
                      }
                      language={analytics?.spend_per_customer.find((v) => v.y.language)?.y.language}
                      loading={isLoadingAnalytics}
                      timezone={program.timezone}
                    />
                    <TrendPlot
                      title="New contacts"
                      step={analytics?.unit}
                      data={analytics?.new_contacts}
                      pastData={pastAnalytics?.new_contacts}
                      loading={isLoadingAnalytics}
                      timezone={program.timezone}
                    />
                  </div>
                  <div>
                    <FeatureFlag flagKey="memberships_enabled">
                      <TrendPlot
                        title="New memberships"
                        step={analytics?.unit}
                        data={analytics?.new_memberships}
                        pastData={pastAnalytics?.new_memberships}
                        loading={isLoadingAnalytics}
                        timezone={program.timezone}
                      />
                    </FeatureFlag>
                    <FeatureFlag flagKey="sessions_enabled">
                      <TrendPlot
                        title="New session credits"
                        step={analytics?.unit}
                        data={analytics?.new_session_credits}
                        pastData={pastAnalytics?.new_session_credits}
                        loading={isLoadingAnalytics}
                        timezone={program.timezone}
                      />
                    </FeatureFlag>
                  </div>
                </FieldCarousel>
              </div>
              <div className="col d-none d-xl-flex py-5">
                <div className="vr mx-auto" />
              </div>
              <div className="col-xl-4 my-5">
                <div className="d-flex justify-content-between">
                  <h5 className="mb-0">Recent Activity</h5>
                  <NavLink to={ROUTES.PROGRAM_ACTIVITY} className="btn btn-link ms-auto p-0">
                    View all
                  </NavLink>
                </div>
                <hr className="mt-2" />
                <ActivityList
                  program={program}
                  activities={activities}
                  loading={isLoadingActivities}
                />
                <CelebrationsList program={program} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
